import { useCallback, useMemo } from 'react'

import { displaySnackBar } from '../../features/snackbar'
import { SnackBarMessage } from '../../features/snackbar/types/SnackBarMessage'
import { useAppDispatch } from '../../hooks/storeHooks'
import { getValidationErrorMessage } from '../helpers/apiValidationError'

type Severity = SnackBarMessage['severity']

export type MutationFeedbackMessages<Result> = {
  success?: string | ((result: Result) => string)
  error: string
  // Used instead of `error` when the backend says which value it refused.
  errorWithReason?: (reason: string) => string
}

export type MutationOutcome<Result> = { ok: true; result: Result } | { ok: false; error: unknown }

/**
 * Snackbar feedback for internal writes. `run` awaits the (already unwrapped) mutation, shows the success or
 * error message and resolves to the outcome instead of throwing.
 */
export const useMutationFeedback = () => {
  const dispatch = useAppDispatch()

  const notify = useCallback((message: string, severity: Severity) => dispatch(displaySnackBar({ message, severity, open: true })), [dispatch])
  const notifySuccess = useCallback((message: string) => notify(message, 'success'), [notify])
  const notifyError = useCallback((message: string) => notify(message, 'error'), [notify])

  const run = useCallback(
    async <Result>(mutation: Promise<Result>, { success, error, errorWithReason }: MutationFeedbackMessages<Result>): Promise<MutationOutcome<Result>> => {
      try {
        const result = await mutation
        if (success) notifySuccess(typeof success === 'function' ? success(result) : success)
        return { ok: true, result }
      } catch (caught) {
        const reason = errorWithReason && getValidationErrorMessage(caught)
        notifyError(reason ? errorWithReason(reason) : error)
        return { ok: false, error: caught }
      }
    },
    [notifyError, notifySuccess]
  )

  return useMemo(() => ({ notify, notifySuccess, notifyError, run }), [notify, notifyError, notifySuccess, run])
}
